/**
 * Audit Logger Utility
 * Logs scoring events, validation flags, and application errors to Supabase
 * All logging is non-blocking and fails silently to avoid disrupting the main application
 */

const SCORING_VERSION = '1.0'

/**
 * Log a scoring-related event to the audit log
 * @param {Object} supabase - Supabase client instance
 * @param {string} assessmentId - Assessment UUID
 * @param {string} eventType - Type of event (score_calculated, response_changed, etc.)
 * @param {Object} eventData - Event-specific data
 * @param {string} userId - User UUID (optional)
 * @returns {Object|null} - Inserted audit log entry or null on error
 */
export async function logScoringEvent(supabase, assessmentId, eventType, eventData, userId = null) {
  try {
    const { data, error } = await supabase.from('assessment_audit_log').insert({
      assessment_id: assessmentId,
      event_type: eventType,
      event_data: {
        ...eventData,
        timestamp: new Date().toISOString()
      },
      scoring_version: SCORING_VERSION,
      created_by: userId
    }).select('id').single()

    if (error) {
      console.error('Audit log insert error:', error)
      return null
    }
    return data
  } catch (err) {
    console.error('Audit logging failed:', err)
    return null
  }
}

/**
 * Log a validation flag for scoring issues
 * @param {Object} supabase - Supabase client instance
 * @param {string} assessmentId - Assessment UUID
 * @param {string} auditLogId - Parent audit log entry UUID (optional)
 * @param {string} checkName - Name of the validation check
 * @param {string} severity - 'info' | 'warning' | 'error'
 * @param {string} message - Human-readable message
 * @param {Object} details - Additional details about the flag
 */
export async function logValidationFlag(supabase, assessmentId, auditLogId, checkName, severity, message, details = {}) {
  try {
    const { error } = await supabase.from('scoring_validation_flags').insert({
      assessment_id: assessmentId,
      audit_log_id: auditLogId,
      check_name: checkName,
      severity,
      message,
      details: {
        ...details,
        detected_at: new Date().toISOString()
      }
    })

    if (error) {
      console.error('Validation flag insert error:', error)
    }
  } catch (err) {
    console.error('Validation flag logging failed:', err)
  }
}

/**
 * Log an application error
 * @param {Object} supabase - Supabase client instance
 * @param {string} errorType - Category of error
 * @param {string} errorMessage - Error message
 * @param {Object} context - Additional context (stackTrace, componentName, assessmentId, etc.)
 */
export async function logAppError(supabase, errorType, errorMessage, context = {}) {
  try {
    const { error } = await supabase.from('app_error_log').insert({
      error_type: errorType,
      error_message: errorMessage,
      stack_trace: context.stackTrace || null,
      context: {
        componentName: context.componentName || null,
        action: context.action || null,
        inputData: context.inputData || null,
        additionalInfo: context.additionalInfo || null
      },
      user_id: context.userId || null,
      assessment_id: context.assessmentId || null,
      url: typeof window !== 'undefined' ? window.location.href : null
    })

    if (error) {
      console.error('Error log insert error:', error)
    }
  } catch (err) {
    console.error('Error logging failed:', err)
  }
}

/**
 * Resolve a validation flag
 * @param {Object} supabase - Supabase client instance
 * @param {string} flagId - Validation flag UUID
 * @param {string} userId - User resolving the flag
 */
export async function resolveValidationFlag(supabase, flagId, userId) {
  try {
    const { error } = await supabase.from('scoring_validation_flags').update({
      resolved: true,
      resolved_at: new Date().toISOString(),
      resolved_by: userId
    }).eq('id', flagId)

    if (error) {
      console.error('Validation flag resolve error:', error)
      throw error
    }
  } catch (err) {
    console.error('Failed to resolve validation flag:', err)
    throw err
  }
}

/**
 * Get audit history for an assessment
 * @param {Object} supabase - Supabase client instance
 * @param {string} assessmentId - Assessment UUID
 * @param {number} limit - Max number of entries to return
 * @returns {Array} - Array of audit log entries
 */
export async function getAuditHistory(supabase, assessmentId, limit = 50) {
  try {
    const { data, error } = await supabase
      .from('assessment_audit_log')
      .select('*')
      .eq('assessment_id', assessmentId)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) {
      console.error('Failed to fetch audit history:', error)
      return []
    }
    return data || []
  } catch (err) {
    console.error('Audit history fetch failed:', err)
    return []
  }
}

/**
 * Get validation flags for an assessment
 * @param {Object} supabase - Supabase client instance
 * @param {string} assessmentId - Assessment UUID
 * @param {boolean} includeResolved - Whether to include resolved flags
 * @returns {Array} - Array of validation flags
 */
export async function getValidationFlags(supabase, assessmentId, includeResolved = false) {
  try {
    let query = supabase
      .from('scoring_validation_flags')
      .select('*')
      .eq('assessment_id', assessmentId)
      .order('created_at', { ascending: false })

    if (!includeResolved) {
      query = query.eq('resolved', false)
    }

    const { data, error } = await query

    if (error) {
      console.error('Failed to fetch validation flags:', error)
      return []
    }
    return data || []
  } catch (err) {
    console.error('Validation flags fetch failed:', err)
    return []
  }
}

/**
 * Get validation flag counts by severity
 * @param {Object} supabase - Supabase client instance
 * @param {string} assessmentId - Assessment UUID
 * @returns {Object} - Counts by severity { info, warning, error, total }
 */
export async function getValidationFlagCounts(supabase, assessmentId) {
  try {
    const flags = await getValidationFlags(supabase, assessmentId, false)
    return {
      info: flags.filter(f => f.severity === 'info').length,
      warning: flags.filter(f => f.severity === 'warning').length,
      error: flags.filter(f => f.severity === 'error').length,
      total: flags.length
    }
  } catch (err) {
    console.error('Failed to get validation flag counts:', err)
    return { info: 0, warning: 0, error: 0, total: 0 }
  }
}
